'use client';
import React, { useEffect, useState, useRef } from 'react';
import { Layout, message } from 'antd';
import Sidebar from './components/Sidebar';
import ChatHeader from './components/ChatHeader';
import ChatContent from './components/ChatContent';
import ChatFooter from './components/ChatFooter';
import { Message, MessageGroup, MessagesHistory } from '../../types/chat';
import { Messages1, Messages2, ArrowUp, Danger } from 'iconsax-react';
import {
  setReceiverId,
  setSelectedRoomId,
  setSelectedUser,
  useChat,
} from '../../store/slices/chat';
import { socket } from '../../lib/socket';
import { useDispatch, useSelector } from 'react-redux';
import InfoText from '../../components/atoms/InfoText';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useGetChatHistoryQuery } from '../../store/services/chatService';
import { RootState } from '../../store/store';

const { Header, Footer, Sider, Content } = Layout;

const Chat = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const chatRoomId = searchParams.get('chatRoomId');
  const [myMessages, setMyMessages] = useState<MessageGroup[]>();
  const { selectedUser, selectedRoomId, isClosed } = useChat();
  const effectiveRoomId = chatRoomId || selectedRoomId;
  const [typing, setTyping] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const userId = user?.adminData?.id;
  const token = useSelector((state: RootState) => state.auth.token);

  // Sync chatRoomId from URL query param to Redux store
  useEffect(() => {
    if (chatRoomId && chatRoomId !== selectedRoomId) {
      dispatch(setSelectedRoomId(chatRoomId));
    }
  }, [chatRoomId, selectedRoomId, dispatch]);

  useEffect(() => {
    if (userId && token) {
      socket.io.opts.query = {
        profileId: userId,
        token: token,
      };
      socket.connect();
    }

    const markMessagesAsSeen = (
      prevMessages: MessageGroup[] | undefined,
      targetMsgId?: string
    ): MessageGroup[] | undefined => {
      if (!prevMessages) return prevMessages;
      return prevMessages.map((group) => ({
        ...group,
        messages: group.messages.map((message) => {
          if (targetMsgId) {
            if (
              String(message.id) === String(targetMsgId) ||
              String(message.messageId) === String(targetMsgId)
            ) {
              return { ...message, read: true };
            }
            return message;
          }
          if (String(message?.sender?.id) === String(userId)) {
            return { ...message, read: true };
          }
          return message;
        }),
      }));
    };

    const handleUserMessage = (msg: string | Message) => {
      const parsedMsg: Message =
        typeof msg === 'string' ? JSON.parse(msg) : msg;
      if (parsedMsg?.status === 'SENT') {
        if (String(parsedMsg?.roomId) === String(effectiveRoomId)) {
          const isFromOtherUser =
            String(parsedMsg?.sender?.id) !== String(userId);

          setMyMessages((prevMessages) => {
            if (!prevMessages) {
              return [{ date: '', messages: [parsedMsg] }];
            }

            const base = isFromOtherUser
              ? prevMessages.map((group) => ({
                  ...group,
                  messages: group.messages.map((m) =>
                    String(m?.sender?.id) === String(userId)
                      ? { ...m, read: true }
                      : m
                  ),
                }))
              : prevMessages;

            return [...base, { date: '', messages: [parsedMsg] }];
          });
          setTimeout(() => {
            scrollToBottom();
          }, 50);
        }
      } else {
        message.error(t('messageNotSent'));
      }
    };

    type ChatEvent = {
      isTyping?: boolean;
      type?: 'typing' | 'seen' | 'read';
      messageId?: string;
      id?: string;
      roomId?: string | number;
      receiverId?: string | number;
      read?: boolean;
    };

    const handleChatEvents = (event: ChatEvent) => {
      const isCurrentRoom =
        (event?.roomId && String(event.roomId) === String(effectiveRoomId)) ||
        (event?.receiverId &&
          String(event.receiverId) === String(selectedUser?.id));

      if (isCurrentRoom) {
        if (typeof event.isTyping === 'boolean') {
          setTyping(event.isTyping);
        }

        const isReadEvent =
          event.type === 'seen' || event.type === 'read' || !!event.read;
        const targetMsgId = event.messageId || event.id;

        if (isReadEvent) {
          setMyMessages((prev) => markMessagesAsSeen(prev, targetMsgId));
        }
      }
    };

    socket.on('user-messages', handleUserMessage);
    socket.on('chat-events', handleChatEvents);

    return () => {
      socket.off('user-messages', handleUserMessage);
      socket.off('chat-events', handleChatEvents);
      socket.disconnect();
    };
  }, [effectiveRoomId, selectedUser?.id, userId]);

  // Reset chat data when user leave the route
  useEffect(() => {
    return () => {
      dispatch(setSelectedUser(undefined));
      dispatch(setReceiverId(undefined));
      dispatch(setSelectedRoomId(''));
      sessionStorage.setItem('chatState', JSON.stringify({}));
    };
  }, []);

  const {
    data: messagesHistory,
    isFetching,
    isLoading,
  } = useGetChatHistoryQuery(
    { roomId: effectiveRoomId, page: page },
    { refetchOnMountOrArgChange: true, skip: !effectiveRoomId }
  );

  const scrollToBottom = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const handleLoadMore = () => {
    if (!isLoadingMore && messagesHistory?.payload?.hasMoreMessages) {
      setIsLoadingMore(true);
      setPage((prev) => prev + 1);
    }
  };

  const normalizeMessagesHistory = (
    groups: MessageGroup[]
  ): MessageGroup[] => {
    let seenOtherUserLater = false;
    const cloned = groups.map((g) => ({
      ...g,
      messages: g.messages.map((m) => ({ ...m })),
    }));

    for (let i = cloned.length - 1; i >= 0; i--) {
      const group = cloned[i];
      for (let j = group.messages.length - 1; j >= 0; j--) {
        const msg = group.messages[j];
        const isOtherUser = String(msg?.sender?.id) !== String(userId);
        if (isOtherUser) {
          seenOtherUserLater = true;
        } else if (seenOtherUserLater) {
          msg.read = true;
        }
      }
    }

    return cloned;
  };

  useEffect(() => {
    if (messagesHistory?.payload?.messagesGrouping) {
      const normalized = normalizeMessagesHistory(
        messagesHistory.payload.messagesGrouping
      );
      if (page === 0) {
        setMyMessages(normalized);
        // Scroll to bottom on first load
        setTimeout(scrollToBottom, 100);
      } else {
        setMyMessages((prev) => {
          if (!prev) return normalized;
          return [...normalized, ...prev];
        });
      }

      setIsLoadingMore(false);
    }
  }, [messagesHistory, userId]);

  // Reset pagination when room changes
  useEffect(() => {
    setPage(0);
  }, [effectiveRoomId]);

  //? Componenets styles
  const headerStyle: React.CSSProperties = {
    backgroundColor: 'white',
    padding: 0,
    height: 'fit-content',
    borderRadius: '0 8px 0 0 ',
  };

  const contentStyle: React.CSSProperties = {
    textAlign: 'center',
    lineHeight: '2',
    scrollbarWidth: 'thin',
    scrollbarColor: '#fd9ffd transparent',
  };

  const siderStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '10px',
    border: '1px solid #ddd',
    backgroundColor: 'white',
    scrollbarWidth: 'thin',
    scrollbarColor: '#fd9ffd transparent',
    height: '100%',
    overflow: 'hidden',
  };

  const footerStyle: React.CSSProperties = {
    textAlign: 'center',
    backgroundColor: 'transparent',
    padding: 8,
    minHeight: 'fit-content',
  };

  const layoutStyle: React.CSSProperties = {
    borderRadius: 8,
    overflow: 'hidden',
    background: 'transparent',
    height: '100%',
  };

  return (
    <div className="h-[calc(100vh-112px)] flex flex-1 overflow-hidden p-1">
      <Layout style={layoutStyle} className="h-full w-full bg-transparent overflow-hidden">
        <Sider width="30%" style={siderStyle} className="h-full">
          <Sidebar />
        </Sider>
        {!!effectiveRoomId ? (
          <Layout className="flex flex-col flex-1 h-full overflow-hidden" style={layoutStyle}>
            <Header style={headerStyle} className="shrink-0">
              <ChatHeader />
            </Header>
            <Content
              ref={contentRef}
              className="overflow-y-auto flex flex-col p-2 flex-1 min-h-0"
              style={contentStyle}
            >
              {messagesHistory?.payload?.hasMoreMessages &&
                !isFetching &&
                !isLoading && (
                  <div className="flex justify-center py-2 shrink-0">
                    <button
                      onClick={handleLoadMore}
                      disabled={isLoadingMore}
                      className="flex items-center gap-2 p-4 rounded-full bg-soft-background dark:bg-soft-dark-background text-light-text-primary dark:text-dark-text-primary hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ArrowUp size={16} />
                    </button>
                  </div>
                )}

              <ChatContent
                chatLoading={isFetching || isLoading}
                messages={myMessages}
              />
            </Content>
            {!!effectiveRoomId && (
              <Footer style={footerStyle} className="shrink-0">
                {isClosed ? (
                  <div className="flex items-center justify-center gap-2 h-full">
                    <Danger color="#f58f29" />
                    <InfoText
                      text={t('ticketClosed')}
                      className="text-light-alert"
                    />
                  </div>
                ) : (
                  <>
                    {typing && (
                      <p className="animate-bounce transition-all delay-200 flex items-center gap-1 m-2 text-text-secondary dark:text-dark-text-secondary">
                        <Messages2 size={16} /> <span>{t('typing')}</span>
                      </p>
                    )}
                    <ChatFooter roomId={effectiveRoomId} />
                  </>
                )}
              </Footer>
            )}
          </Layout>
        ) : (
          <div className="flex flex-col items-center justify-center h-full w-full">
            <Messages1 size={200} variant="Bulk" className="text-pink-400" />
            <InfoText text={t('clickOnAnyUser')} />
          </div>
        )}
      </Layout>
    </div>
  );
};

export default Chat;
